/**
 * Sticky Cart Bar - Modern Implementation
 * Features:
 * - Intersection Observer for performance
 * - Full accessibility support
 * - Progressive enhancement
 * - Conversion optimization
 * - Zero global layout impact
 */

class StickyCartBar extends HTMLElement {
  constructor() {
    super();

// Bind methods
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleEscapeKey = this.handleEscapeKey.bind(this);

// State
    this.isVisible = false;
    this.isSubmitting = false;
    this.config = null;
    this.observer = null;
    this.targetElement = null;

// DOM references
    this.form = null;
    this.button = null;
    this.buttonText = null;
    this.variantInput = null;
    this.priceContainer = null;
  }

  connectedCallback() {

// Parse configuration
    this.config = this.parseConfig();
    if (!this.config) {
      console.warn('Sticky cart bar: Invalid configuration');
      return;
    }

// Cache DOM elements
    this.cacheElements();

// Set up intersection observer
    this.setupIntersectionObserver();

// Set up event listeners
    this.setupEventListeners();

// Initial state
    this.updateVisibility(false);

// Announce to screen readers
    this.announceToScreenReader('Sticky cart bar loaded');
  }

  disconnectedCallback() {

// Clean up
    this.cleanup();
  }

  parseConfig() {
    try {
      const configElement = this.querySelector('[data-sticky-cart-config]');
      return configElement
        ? JSON.parse(configElement.textContent)
        : null;
    } catch (error) {
      console.error('Failed to parse sticky cart config:', error);
      return null;
    }
  }

  cacheElements() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[data-add-to-cart]');
    this.buttonText = this.querySelector('[data-button-text]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.priceContainer = this.querySelector('[data-price-container]');
  }

  setupIntersectionObserver() {

// Find the target element to observe
    this.targetElement = document.querySelector(this.config.observeSelector);

    if (!this.targetElement) {
      console.warn(`Sticky cart bar: Target element "${
        this.config.observeSelector
      }" not found`);
      return;
    }

// Create intersection observer with optimized options
    const options = {
      root: null, // viewport
      rootMargin: '0px 0px -10px 0px', // Small buffer to prevent flickering
      threshold: [0, 0.1] // Trigger when completely hidden or 10% visible
    };

    this.observer = new IntersectionObserver(this.handleIntersection, options);
    this.observer.observe(this.targetElement);
  }

  setupEventListeners() {

// Form submission
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit);
    }

// Variant changes from main product form
    document.addEventListener(`variant-change:${
      this.config.sectionId
    }`, this.handleVariantChange);

// Cart updates
    document.addEventListener('cart:updated', this.handleCartUpdate);

// Escape key to hide (accessibility)
    document.addEventListener('keydown', this.handleEscapeKey);

// Focus management
    this.addEventListener('focusin', this.handleFocusIn.bind(this));
    this.addEventListener('focusout', this.handleFocusOut.bind(this));
  }

  handleIntersection(entries) {
    const entry = entries[0];
    const shouldShow = ! entry.isIntersecting;

// Debounce to prevent rapid toggling
    clearTimeout(this.visibilityTimeout);
    this.visibilityTimeout = setTimeout(() => {
      this.updateVisibility(shouldShow);
    }, 50);
  }

  updateVisibility(shouldShow) {
    if (this.isVisible === shouldShow) return;
    

    this.isVisible = shouldShow;

// Update DOM
    this.setAttribute('data-visible', shouldShow.toString());
    this.hidden = !shouldShow;

// Accessibility announcements
    if (shouldShow) {
      this.announceToScreenReader('Sticky add to cart bar is now visible');
    }

// Fire custom event
    this.dispatchEvent(new CustomEvent('sticky-cart:visibility-change', {
      detail: {
        visible: shouldShow
      },
      bubbles: true
    }));
  }

  handleVariantChange(event) {
    const {variant} = event.detail;

    if (!variant) {
      this.updateButton(false, this.config.texts.unavailable);
      return;
    }

// Update variant ID
    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

// Update button state
    const isAvailable = variant.available;
    const buttonText = isAvailable
      ? this.config.texts.addToCart
      : this.config.texts.soldOut;

    this.updateButton(isAvailable, buttonText);

// Update price
    this.updatePrice(variant);
  }

  updateButton(isEnabled, text) {
    if (!this.button || !this.buttonText) return;
    

    this.button.disabled = !isEnabled;
    this.buttonText.textContent = text;

// Update ARIA label
    this.button.setAttribute('aria-label', `${text} - ${
      this.config.texts.addToCart
    }`);
  }

  updatePrice(variant) {
    if (!this.priceContainer || !variant.price) return;
    

// Format price
    const price = this.formatMoney(variant.price);
    let priceHTML = `<span class="price__current">${price}</span>`;

// Add compare at price if on sale
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const comparePrice = this.formatMoney(variant.compare_at_price);
      priceHTML = `
        <span class="price__current price__current--on-sale">${price}</span>
        <span class="price__compare">${comparePrice}</span>
      `;
    }

    this.priceContainer.innerHTML = priceHTML;
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (this.isSubmitting) return;
    

    try {
      this.setLoadingState(true);

// Add to cart
      const result = await this.addToCart();

// Show success state
      this.showSuccessState();

// Fire cart added event - use the same event as regular product forms
      this.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        bubbles: true,
        detail: {
          product: result,
          source: 'sticky-cart-bar'
        }
      }));

// Hide sticky bar temporarily (cart drawer will open)
      this.updateVisibility(false);

// Re-enable after cart interaction
      setTimeout(() => {
        this.updateVisibility(!this.targetElement ?. getBoundingClientRect().intersecting);
      }, 2000);

    } catch (error) {
      console.error('Failed to add to cart:', error);
      this.showErrorState(error.message);
    } finally {
      this.setLoadingState(false);
    }
  }

  async addToCart() {
    const formData = new FormData(this.form);

// Add sections for cart drawer update
    formData.append('sections', 'cart-drawer,cart-icon-bubble');
    formData.append('sections_url', window.location.pathname);

    const response = await fetch(`${
      window.Shopify ?. routes ?. root || '/'
    }cart/add.js`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    });

    if (! response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.description || 'Failed to add to cart');
    }

    return response.json();
  }

  setLoadingState(isLoading) {
    this.isSubmitting = isLoading;
    this.button ?. setAttribute('data-loading', isLoading.toString());
    this.button ?. setAttribute('aria-busy', isLoading.toString());

    if (isLoading) {
      this.announceToScreenReader('Adding to cart');
    }
  }

  showSuccessState() {
    this.button ?. setAttribute('data-success', 'true');
    this.announceToScreenReader(this.config.texts.added);

// Reset success state
    setTimeout(() => {
      this.button ?. removeAttribute('data-success');
    }, 2000);
  }

  showErrorState(message) {
    this.announceToScreenReader(`Error: ${message}`);

// Show error in button temporarily
    const originalText = this.buttonText ?. textContent;
    if (this.buttonText) {
      this.buttonText.textContent = 'Error';
      setTimeout(() => {
        this.buttonText.textContent = originalText;
      }, 2000);
    }
  }

  handleCartUpdate() {

// Refresh price/availability from main form if needed
    this.dispatchEvent(new CustomEvent('sticky-cart:refresh-needed', {bubbles: true}));
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape' && this.isVisible) {
      this.updateVisibility(false);
      this.targetElement ?. focus();
    }
  }

  handleFocusIn() {

// Add focus class for styling
    this.classList.add('has-focus');
  }

  handleFocusOut(event) {

// Remove focus class if focus is leaving the component
    if (!this.contains(event.relatedTarget)) {
      this.classList.remove('has-focus');
    }
  }

  announceToScreenReader(message) {

// Create live region for announcements
    if (!this.liveRegion) {
      this.liveRegion = document.createElement('div');
      this.liveRegion.setAttribute('aria-live', 'polite');
      this.liveRegion.setAttribute('aria-atomic', 'true');
      this.liveRegion.className = 'visually-hidden';
      document.body.appendChild(this.liveRegion);
    }

    this.liveRegion.textContent = message;

// Clear after announcement
    setTimeout(() => {
      this.liveRegion.textContent = '';
    }, 1000);
  }

  formatMoney(cents) {

// Use Shopify's money formatting if available
    if (window.Shopify ?. formatMoney) {
      return window.Shopify.formatMoney(cents);
    }

// Fallback formatting
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }

  cleanup() {

// Remove intersection observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

// Clear timeouts
    clearTimeout(this.visibilityTimeout);

// Remove event listeners
    document.removeEventListener(`variant-change:${
      this.config ?. sectionId
    }`, this.handleVariantChange);
    document.removeEventListener('cart:updated', this.handleCartUpdate);
    document.removeEventListener('keydown', this.handleEscapeKey);

// Remove live region
    if (this.liveRegion) {
      document.body.removeChild(this.liveRegion);
      this.liveRegion = null;
    }
  }
}

// Register custom element
customElements.define('sticky-cart-bar', StickyCartBar);

// Progressive enhancement check
if (!window.IntersectionObserver) {
  console.warn('Sticky cart bar: IntersectionObserver not supported, feature disabled');
}